import { useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { CanAccess, useDataProvider, useTranslate } from "ra-core";
import { Link, Navigate } from "react-router";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { useIsMobile } from "@/hooks/use-mobile";
import { MobileContent } from "../layout/MobileContent";
import MobileHeader from "../layout/MobileHeader";
import { MobileBackButton } from "../misc/MobileBackButton";

import {
  ListCardsSkeleton,
  ListEmptyState,
  ListErrorState,
} from "../design/ListStates";
import { StatusPill } from "../design/StatusPill";
import type {
  Deal,
  LeadSource,
  MetaLeadIngestion,
  MetaWebhookEvent,
} from "../types";
import { MetaFormProjectsEditor } from "./MetaFormProjectsEditor";
import {
  META_EVENTS_PAGE_SIZE,
  META_WEBHOOK_RESULTS,
  assignmentOutcomeKey,
  buildEventsFilter,
  buildIngestionsFilter,
  isSourceMatched,
  projectChoiceKey,
  resultLabelKey,
  signatureLabelKey,
} from "./metaLeadsRules";

const QUERY_KEY = "meta_leads_status";
const SELECT_CLASS =
  "h-11 w-full rounded-md border bg-background px-3 text-sm sm:h-9";

/**
 * Almikia Epic E9 (TASK-049): direccion-only "Leads de Meta" screen, reached from Settings. A
 * newest-first, paginated list of the received Meta webhooks with the per-lead outcome of the
 * processed ones, plus the form -> project mapping editor. The raw payload is never selected
 * nor rendered, and nothing counts, ranks or compares advisors. RLS and the provider guard are
 * the real authority; `CanAccess` only redirects the other roles away.
 */
export const MetaLeadsStatus = () => (
  <CanAccess
    resource="meta_webhook_events"
    action="list"
    accessDenied={<Navigate to="/" replace />}
  >
    <MetaLeadsStatusContent />
  </CanAccess>
);

MetaLeadsStatus.path = "/meta_leads";

const formatWhen = (iso: string) =>
  new Intl.DateTimeFormat("es-MX", {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: "America/Mexico_City",
  }).format(new Date(iso));

type EventsPage = {
  events: MetaWebhookEvent[];
  total: number;
  ingestions: MetaLeadIngestion[];
  deals: Deal[];
};

const MetaLeadsStatusContent = () => {
  const translate = useTranslate();
  const isMobile = useIsMobile();
  const dataProvider = useDataProvider();
  const [result, setResult] = useState("");
  const [page, setPage] = useState(1);

  const sources = useQuery({
    queryKey: [QUERY_KEY, "lead_sources"],
    queryFn: async () =>
      (
        await dataProvider.getList<LeadSource>("lead_sources", {
          pagination: { page: 1, perPage: 200 },
          sort: { field: "name", order: "ASC" },
          filter: {},
        })
      ).data,
  });
  const log = useQuery({
    queryKey: [QUERY_KEY, "events", result, page],
    placeholderData: keepPreviousData,
    queryFn: async (): Promise<EventsPage> => {
      // `raw_body` is deliberately not selected: personal data never reaches the screen.
      const events = await dataProvider.getList<MetaWebhookEvent>(
        "meta_webhook_events",
        {
          pagination: { page, perPage: META_EVENTS_PAGE_SIZE },
          sort: { field: "received_at", order: "DESC" },
          filter: buildEventsFilter(result),
          meta: {
            columns: [
              "id",
              "received_at",
              "signature_valid",
              "result",
              "error",
            ],
          },
        },
      );
      const eventIds = events.data.map((event) => event.id);
      if (eventIds.length === 0) {
        return {
          events: [],
          total: events.total ?? 0,
          ingestions: [],
          deals: [],
        };
      }
      const ingestions = await dataProvider.getList<MetaLeadIngestion>(
        "meta_lead_ingestions",
        {
          pagination: { page: 1, perPage: META_EVENTS_PAGE_SIZE * 4 },
          sort: { field: "created_at", order: "DESC" },
          filter: buildIngestionsFilter(eventIds),
        },
      );
      const dealIds = ingestions.data.map((row) => row.deal_id);
      const deals =
        dealIds.length === 0
          ? []
          : (await dataProvider.getMany<Deal>("deals", { ids: dealIds })).data;
      return {
        events: events.data,
        total: events.total ?? events.data.length,
        ingestions: ingestions.data,
        deals,
      };
    },
  });

  const changeResult = (value: string) => {
    setResult(value);
    setPage(1);
  };

  const events = log.data?.events ?? [];
  const total = log.data?.total ?? 0;
  const hasNext = page * META_EVENTS_PAGE_SIZE < total;
  const sourceName = (deal: Deal | undefined) =>
    sources.data?.find((source) => source.id === deal?.lead_source_id)?.name;

  const intro = (
    <p className="text-sm text-muted-foreground">
      {translate("crm.metaLeads.intro")}
    </p>
  );

  const content = (
    <>
      <Card>
        <CardContent className="space-y-3 p-4">
          <h2 className="text-lg font-semibold">
            {translate("crm.metaLeads.status_title")}
          </h2>
          <div className="space-y-2">
            <Label htmlFor="meta-result-filter">
              {translate("crm.metaLeads.filter_result")}
            </Label>
            <select
              id="meta-result-filter"
              className={SELECT_CLASS}
              value={result}
              onChange={(event) => changeResult(event.target.value)}
            >
              <option value="">{translate("crm.metaLeads.all")}</option>
              {META_WEBHOOK_RESULTS.map((value) => (
                <option key={value} value={value}>
                  {translate(resultLabelKey(value))}
                </option>
              ))}
            </select>
          </div>

          {log.error ? (
            <ListErrorState
              message={translate("crm.common.list_error")}
              onRetry={() => log.refetch()}
            />
          ) : !log.data ? (
            <ListCardsSkeleton rows={3} />
          ) : events.length === 0 ? (
            <ListEmptyState
              className="min-h-0 py-4"
              title={translate("crm.metaLeads.empty")}
              description={translate("crm.metaLeads.empty_hint")}
            />
          ) : (
            <ul className="space-y-2" data-testid="meta-events-list">
              {events.map((event) => {
                const ingestion = log.data?.ingestions.filter(
                  (row) => String(row.event_id) === String(event.id),
                );
                return (
                  <li key={event.id} data-testid="meta-event-row">
                    <div className="space-y-2 rounded-md border p-3">
                      <div className="flex flex-wrap items-center justify-between gap-2">
                        <span className="text-sm text-muted-foreground">
                          {formatWhen(event.received_at)}
                        </span>
                        <div className="flex flex-wrap items-center gap-2">
                          <StatusPill
                            tone={event.signature_valid ? "ok" : "not-ok"}
                            text={translate(
                              signatureLabelKey(event.signature_valid),
                            )}
                          />
                          <span className="text-sm font-medium">
                            {translate(resultLabelKey(event.result))}
                          </span>
                        </div>
                      </div>
                      {event.error ? (
                        <p className="break-words text-sm text-destructive">
                          {event.error}
                        </p>
                      ) : null}
                      {(ingestion ?? []).map((row) => {
                        const deal = log.data?.deals.find(
                          (candidate) =>
                            String(candidate.id) === String(row.deal_id),
                        );
                        return (
                          <div
                            key={row.leadgen_id}
                            data-testid="meta-ingestion"
                            className="space-y-1 border-t pt-2 text-sm"
                          >
                            <Link
                              to={`/deals/${row.deal_id}/show`}
                              className="font-medium underline-offset-2 hover:underline"
                            >
                              {deal?.name ?? translate("crm.metaLeads.lead")}
                            </Link>
                            <p className="text-muted-foreground">
                              {[
                                isSourceMatched(row.source_choice)
                                  ? (sourceName(deal) ??
                                    translate("crm.metaLeads.unclassified"))
                                  : translate("crm.metaLeads.unclassified"),
                                translate(projectChoiceKey(row.project_choice)),
                                translate(
                                  assignmentOutcomeKey(row.assignment_outcome),
                                ),
                              ].join(" · ")}
                            </p>
                          </div>
                        );
                      })}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}

          {total > META_EVENTS_PAGE_SIZE ? (
            <div className="flex items-center justify-between gap-3">
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                disabled={page === 1}
                onClick={() => setPage((current) => current - 1)}
              >
                {translate("crm.metaLeads.previous")}
              </Button>
              <Button
                type="button"
                variant="outline"
                className="h-11 sm:h-9"
                disabled={!hasNext}
                onClick={() => setPage((current) => current + 1)}
              >
                {translate("crm.metaLeads.next")}
              </Button>
            </div>
          ) : null}
        </CardContent>
      </Card>

      <MetaFormProjectsEditor />
    </>
  );

  if (isMobile) {
    return (
      <>
        <MobileHeader>
          <MobileBackButton to="/settings" />
          <div className="flex flex-1 min-w-0">
            <h1 className="text-xl font-semibold truncate">
              {translate("crm.metaLeads.title")}
            </h1>
          </div>
        </MobileHeader>
        <MobileContent>
          <div className="space-y-4">
            {intro}
            {content}
          </div>
        </MobileContent>
      </>
    );
  }

  return (
    <div className="mx-auto mt-2 max-w-3xl space-y-4 pb-8">
      <div className="px-1">
        <Link
          to="/settings"
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
        >
          {translate("crm.metaLeads.back")}
        </Link>
        <h1 className="text-xl font-semibold">
          {translate("crm.metaLeads.title")}
        </h1>
        {intro}
      </div>
      {content}
    </div>
  );
};
